import { Api } from '@automapa/svelte-automapa-template';

import { StatsRecipientsApi, recipientErrorKey, type RecipientType } from './StatsRecipientsApi';

/**
 * Stan zakładki 'stats-recipients' na /admin (wzorzec UsageStatsApp).
 *
 * Żadna metoda nie rzuca - odmowa backendu ląduje w `_error` jako KLUCZ i18n (a nie surowy tekst
 * odpowiedzi), żeby komponent mógł go podać wprost do `$_()`. Dzięki temu duplikat adresu czy brak
 * uprawnień pokazują konkretny komunikat, a nie ogólny błąd.
 */
export class StatsRecipientsApp {
    private readonly api: Api;
    private recipients: RecipientType[] = [];
    private _isLoading: boolean = true;
    private _error: string | null = null;

    constructor(api: Api) {
        this.api = api;
    }

    public async load(): Promise<void> {
        this._isLoading = true;
        await this.refresh();
        this._isLoading = false;
    }

    /**
     * Dodaje adres i - po sukcesie - odświeża listę w stanie.
     *
     * Zwraca `true`/`false` zamiast rzucać, bo komponent musi wiedzieć, czy wyczyścić pole
     * formularza. Lista wraca z backendu w całości, a nie doklejeniem wiersza z odpowiedzi:
     * kolejność (`ORDER BY id`) i format `created_at` należą do bazy, więc jedno źródło prawdy
     * jest tańsze niż wstawianie wiersza w odpowiednie miejsce po stronie przeglądarki.
     */
    public async add(email: string): Promise<boolean> {
        this._error = null;

        try {
            await StatsRecipientsApi.addRecipient(this.api, email);
        } catch (error) {
            this._error = recipientErrorKey(error);
            console.error('Error adding stats recipient:', error);

            return false;
        }

        await this.refresh();

        return this._error === null;
    }

    public async remove(id: number): Promise<boolean> {
        this._error = null;

        try {
            await StatsRecipientsApi.removeRecipient(this.api, id);
        } catch (error) {
            this._error = recipientErrorKey(error);
            console.error('Error removing stats recipient:', error);

            return false;
        }

        await this.refresh();

        return this._error === null;
    }

    /**
     * Pobiera listę, zostawiając `_isLoading` w spokoju - spinner pierwszego wejścia nie ma
     * zasłaniać tabeli przy odświeżeniu po dodaniu czy usunięciu adresu.
     */
    private async refresh(): Promise<void> {
        this._error = null;

        try {
            this.recipients = await StatsRecipientsApi.getRecipients(this.api);
        } catch (error) {
            this._error = recipientErrorKey(error);
            console.error('Error loading stats recipients:', error);
        }
    }

    public getRecipients(): RecipientType[] {
        return this.recipients;
    }

    public isLoading(): boolean {
        return this._isLoading;
    }

    /**
     * Pusto znaczy tu "lista wczytana i naprawdę nie ma odbiorców" - stan, w którym cron nie wyśle
     * maila i trzeba o tym ostrzec. Nieudane pobranie zostawia listę pustą technicznie, ale
     * ostrzeżenie byłoby wtedy kłamstwem, więc błąd i ładowanie odsiewamy.
     */
    public isEmpty(): boolean {
        return !this._isLoading && this._error === null && this.recipients.length === 0;
    }

    public getError(): string | null {
        return this._error;
    }
}
